import React, { useEffect, useState } from 'react';
import { Box, Container, Typography, Button, Paper, Divider, Chip, CircularProgress } from '@mui/material';
import { CheckCircleOutline, Email, ContentCopy, VerifiedUser, ReceiptLong } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { invokeApi, apiList } from '../../services/apiServices';
import { InvoiceModal, PURPLE } from '../common/InvoiceDocument';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orderId, setOrderId] = useState('');
  const [paymentStatus, setPaymentStatus] = useState('Paid');
  const [isVerifying, setIsVerifying] = useState(false);
  const [copied, setCopied] = useState(false);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  // Retrieve any state passed from checkout
  const orderDetails = location.state || {};

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const queryOrderId = queryParams.get('order_id') || queryParams.get('orderId');

    const activeOrderId = queryOrderId || location.state?.orderId || ('ORD-' + Math.floor(100000 + Math.random() * 900000));
    setOrderId(activeOrderId);

    // If redirected from Cashfree with order_id, verify payment with backend
    if (queryOrderId) {
      setIsVerifying(true);
      invokeApi(apiList.verifyCashfreePayment, { order_id: queryOrderId })
        .then((res) => {
          if (res?.data?.is_paid || res?.data?.order_status === 'PAID') {
            setPaymentStatus('Paid');
          } else if (res?.data?.order_status) {
            setPaymentStatus(res.data.order_status);
          }
        })
        .catch((err) => {
          console.error('Error verifying Cashfree payment:', err);
        })
        .finally(() => {
          setIsVerifying(false);
        });
    }
  }, [location]);

  const handleCopy = () => {
    if (orderId) {
      navigator.clipboard.writeText(orderId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const invoiceData = {
    invoice_number: `AR-IN-${orderId.replace(/[^0-9]/g, '').slice(-6) || '774201'}`,
    customer_name: orderDetails.customerName || 'Valued Customer',
    customer_number: orderDetails.phone || '9901991771',
    customer_email: orderDetails.email || '',
    address: orderDetails.address || 'Bangalore, Karnataka',
    due_date: new Date(),
    payment_status: paymentStatus,
    items: orderDetails.items && orderDetails.items.length > 0 ? orderDetails.items.map(item => ({
      description: item.name || item.description || 'Software Genuine License',
      hsn_sac: '997331',
      quantity: item.qty || item.quantity || 1,
      unit: 'Pcs',
      unit_price: item.price || 990,
      total_price: (item.qty || 1) * (parseFloat(item.price) || 990),
    })) : [
      {
        description: 'Software Genuine Lifetime Retail License',
        hsn_sac: '997331',
        quantity: 1,
        unit: 'Pcs',
        unit_price: orderDetails.totalAmount || 990,
        total_price: orderDetails.totalAmount || 990,
      }
    ],
    total: orderDetails.totalAmount || 990,
    received: orderDetails.totalAmount || 990,
    balance: 0,
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8, textAlign: 'center' }}>
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 260, damping: 20 }}
      >
        <CheckCircleOutline sx={{ fontSize: 90, color: 'success.main', mb: 2 }} />
      </motion.div>
      
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 800, color: '#1A237E' }}>
        Order Confirmed!
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Thank you for your purchase. Your genuine digital license key is being processed.
      </Typography>

      <Paper elevation={0} sx={{ p: 3.5, mb: 3.5, border: '1px solid #E2E8F0', borderRadius: 3, bgcolor: '#F8FAFC' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 1 }}>
            ORDER REFERENCE
          </Typography>
          {isVerifying ? (
            <Chip icon={<CircularProgress size={14} color="inherit" />} label="Verifying Payment..." size="small" variant="outlined" />
          ) : (
            <Chip 
              icon={<VerifiedUser sx={{ fontSize: '16px !important' }} />} 
              label={`Payment: ${paymentStatus}`} 
              color={paymentStatus === 'Paid' ? 'success' : 'primary'} 
              size="small" 
              sx={{ fontWeight: 700 }} 
            />
          )}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, my: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0F172A', letterSpacing: 1 }}>
            {orderId}
          </Typography>
          <Button 
            size="small" 
            onClick={handleCopy} 
            sx={{ minWidth: 32, p: 0.5, color: copied ? 'success.main' : 'text.secondary' }}
          >
            {copied ? <Typography variant="caption" sx={{ fontWeight: 700 }}>COPIED!</Typography> : <ContentCopy sx={{ fontSize: 20 }} />}
          </Button>
        </Box>
        <Divider sx={{ my: 2.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, textAlign: 'left' }}>
          <Email color="primary" sx={{ fontSize: 30, mt: 0.5 }} />
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1E293B' }}>Instant Email Delivery</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5, display: 'block' }}>
              We've dispatched your product license key, activation steps, and tax invoice to your registered email address.
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Action buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={<ReceiptLong />}
          onClick={() => setInvoiceOpen(true)}
          sx={{
            backgroundColor: PURPLE,
            '&:hover': { backgroundColor: '#6454DB' },
            fontWeight: 700,
            textTransform: 'none',
            borderRadius: 2,
            px: 3,
            py: 1,
          }}
        >
          Download Tax Invoice
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/shop')}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
        >
          Continue Shopping
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/track-order')}
          sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 2, px: 3, py: 1 }}
        >
          Track Order
        </Button>
      </Box>

      {/* Invoice Download Modal */}
      <InvoiceModal
        open={invoiceOpen}
        onClose={() => setInvoiceOpen(false)}
        data={invoiceData}
      />
    </Container>
  );
};

export default OrderSuccess;
